import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n, LANGS } from "../lib/i18n.jsx";
import { PageHeader } from "../components/Bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { LogOut, Smartphone, KeyRound, Warehouse, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PersonalData, SizesForm, BadgePanel, Section } from "../components/EmployeeBits";

export default function Profile() {
  const { user, logout, refresh, can } = useAuth();
  const { t, tn, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [emp, setEmp] = useState(null);
  const loadEmp = React.useCallback(() => { if (user?.id) api.get(`/employees/${user.id}/profile`).then((r) => setEmp(r.data)).catch(() => {}); }, [user?.id]);
  React.useEffect(() => { loadEmp(); }, [loadEmp]);
  const [phone, setPhone] = useState(user?.phone || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [installEvt, setInstallEvt] = useState(null);

  React.useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);

  const changeLang = async (l) => {
    setLang(l);
    try { await api.put("/auth/me", { language: l }); } catch {}
  };
  const savePhone = async () => {
    try { await api.put("/auth/me", { phone }); await refresh(); toast.success(t("save")); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const changePw = async () => {
    try { await api.put("/auth/me", pw); setPw({ current_password: "", new_password: "" }); toast.success(t("password_changed")); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <div className="max-w-md mx-auto space-y-4" data-testid="profile-page">
      <PageHeader title={t("profile")} />
      <div className="tactical-card p-4 flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-[var(--amber)] text-white flex items-center justify-center font-bold text-2xl">{(user?.name || "?")[0]}</div>
        <div className="min-w-0">
          <div className="font-display font-bold text-lg truncate" data-testid="profile-name">{user?.name}</div>
          <div className="text-sm text-[var(--text-3)] truncate">{user?.email}</div>
          <div className="text-xs text-[var(--text-3)] mt-0.5">{(user?.roles || []).map((r) => tn(r.name)).join(", ")}</div>
        </div>
      </div>

      <div className="tactical-card p-4 space-y-3">
        <Row label={t("company")} value={user?.company?.name} />
        <Row label={t("position")} value={tn(user?.position?.name) || "—"} />
        <Row label={t("team")} value={tn(user?.team?.name) || "—"} />
        <Row label={t("my_projects")} value={(user?.projects || []).map((p) => p.name).join(", ") || "—"} />
      </div>

      <Section title={t("property_accounting")} icon={Warehouse} testid="profile-property">
        <button onClick={() => nav("/my-property")} className="w-full h-11 flex items-center justify-between px-3 rounded-lg tactical-card text-sm font-semibold" data-testid="profile-my-property">{t("my_property")} <ChevronRight size={16} /></button>
        {can("warehouse.view") && <button onClick={() => nav("/warehouse")} className="w-full h-11 flex items-center justify-between px-3 rounded-lg tactical-card text-sm font-semibold" data-testid="profile-warehouse">{t("asset_registry")} <ChevronRight size={16} /></button>}
      </Section>
      {emp && <PersonalData emp={emp} reload={loadEmp} selfView />}
      {emp && <BadgePanel emp={emp} reload={loadEmp} selfView />}
      {emp && <SizesForm emp={emp} reload={loadEmp} selfView />}

      <div className="tactical-card p-4 space-y-3">
        <label className="text-xs uppercase font-semibold text-[var(--text-3)] block">{t("language")}</label>
        <div className="grid grid-cols-2 gap-2">
          {LANGS.map((l) => (
            <button key={l.code} data-testid={`profile-lang-${l.code}`} onClick={() => changeLang(l.code)}
              className={`h-12 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 ${lang === l.code ? "bg-[var(--amber)] text-white border-transparent" : "tactical-card"}`}>
              <span className="text-xl">{l.flag}</span> {l.name}
            </button>
          ))}
        </div>
        <label className="text-xs uppercase font-semibold text-[var(--text-3)] block pt-2">{t("phone")}</label>
        <div className="flex gap-2">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11" data-testid="profile-phone" />
          <Button onClick={savePhone} className="h-11 bg-[var(--amber)] text-white" data-testid="profile-phone-save">{t("save")}</Button>
        </div>
      </div>

      <div className="tactical-card p-4 space-y-2">
        <div className="text-xs uppercase font-semibold text-[var(--text-3)] flex items-center gap-1"><KeyRound size={13} /> {t("change_password")}</div>
        {!can("users.manage") ? <p className="text-sm text-[var(--text-3)]" data-testid="pw-admin-only">{t("password_admin_only")}</p> : <>
        <Input type="password" placeholder={t("current_password")} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="h-11" data-testid="pw-current" />
        <Input type="password" placeholder={t("new_password")} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="h-11" data-testid="pw-new" />
        <Button onClick={changePw} disabled={!pw.new_password} variant="outline" className="w-full h-11" data-testid="pw-save">{t("change_password")}</Button>
        </>}
      </div>

      <div className="tactical-card p-4">
        <div className="flex items-center gap-2 font-semibold text-sm"><Smartphone size={16} className="text-[var(--amber)]" /> {t("install_app")}</div>
        {installEvt
          ? <Button onClick={() => installEvt.prompt()} className="mt-2 w-full h-11 bg-[var(--sidebar)] text-white" data-testid="install-btn">{t("install_app")}</Button>
          : <p className="text-xs text-[var(--text-3)] mt-1">{isIOS ? t("install_hint_ios") : "Chrome: ⋮ → " + t("install_app")}</p>}
      </div>

      <Button onClick={logout} variant="outline" className="w-full h-12 gap-2 text-[var(--red)]" data-testid="profile-logout"><LogOut size={16} /> {t("logout")}</Button>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-[var(--text-3)]">{label}</span>
      <span className="font-semibold text-right truncate">{value}</span>
    </div>
  );
}
